import { z } from "zod";
import { catalogRecordTypeSchema, catalogRelationshipSchema } from "./v1-2.js";
export const DEFAULT_RESULT_EXPORT_MAX_RECORDS = 2_000;
export const MAX_RESULT_EXPORT_RECORDS = 2_000;
export const MAX_CATALOG_PICKLIST_VALUES = 5_000;
const salesforceApiNameSchema = z.string().regex(/^[A-Za-z][A-Za-z0-9_]*$/);
export const NON_BLOCKING_SCAN_NOTICE_PREFIX = "兼容性提示：";
export function isBlockingScanWarning(message) {
    return !message.startsWith(NON_BLOCKING_SCAN_NOTICE_PREFIX);
}
export const platformRoleSchema = z.enum([
    "platform_admin",
    "skill_admin",
    "auditor",
    "end_user"
]);
export const platformAccountTypeSchema = z.enum(["admin", "user"]);
export const platformUserSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    salesforceUserId: z.string().min(15).max(18).optional(),
    email: z.string().email().max(320).optional(),
    displayName: z.string().min(1).max(160),
    status: z.enum(["invited", "active", "disabled"]),
    accountType: platformAccountTypeSchema.optional(),
    roles: z.array(platformRoleSchema).min(1),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
const profileImageSchema = z.string().max(700_000).refine((value) => /^https:\/\//i.test(value) ||
    /^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/i.test(value), {
    message: "头像必须是 HTTPS 图片或 PNG、JPEG、WebP 图片数据"
});
export const userProfileSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    platformUserId: z.string().uuid(),
    nickname: z.string().trim().min(1).max(80),
    description: z.string().trim().max(240).default(""),
    avatarSource: z.enum(["salesforce", "custom", "initials"]).default("salesforce"),
    avatarUrl: profileImageSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const assistantProfileSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    name: z.string().trim().min(1).max(80),
    description: z.string().trim().max(240),
    welcomeMessage: z.string().trim().min(1).max(1000),
    avatarUrl: profileImageSchema.optional(),
    updatedBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const resultExportFormatSchema = z.enum(["excel", "csv", "png", "svg"]);
export const resultExportPolicySchema = z.object({
    enabled: z.boolean().default(true),
    detailFormats: z.array(resultExportFormatSchema).min(1).max(4).default(["excel", "csv"]),
    groupedFormats: z.array(resultExportFormatSchema).min(1).max(4).default(["excel", "csv"]),
    chartFormats: z.array(resultExportFormatSchema).min(1).max(4).default(["png", "svg", "excel", "csv"]),
    maxRecords: z.preprocess((value) => typeof value === "number" && value > MAX_RESULT_EXPORT_RECORDS ? MAX_RESULT_EXPORT_RECORDS : value, z.number().int().min(1).max(MAX_RESULT_EXPORT_RECORDS).default(DEFAULT_RESULT_EXPORT_MAX_RECORDS))
});
export const assistantPolicySchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    attachmentsEnabled: z.boolean().default(true),
    attachmentBatchWritesEnabled: z.boolean().optional(),
    resultExport: resultExportPolicySchema.optional(),
    updatedBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const userConsentSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    platformUserId: z.string().uuid(),
    salesforceUserId: z.string().min(15).max(18),
    channel: z.literal("salesforce_embedded"),
    termsVersion: z.string().min(1).max(80),
    status: z.enum(["accepted", "revoked"]),
    acceptedAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const salesforceUserBindingSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    platformUserId: z.string().uuid(),
    salesforceUserId: z.string().min(15).max(18),
    status: z.enum(["active", "disabled"]),
    source: z.enum(["admin", "migration"]),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
const entitlementRoleKeySchema = z.string().regex(/^[a-z][a-z0-9_-]{1,79}$/);
export const userEntitlementSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    platformUserId: z.string().uuid(),
    status: z.enum(["active", "suspended", "revoked"]),
    scenarioIds: z.array(z.string().uuid()).max(100).default([]),
    roleKeys: z.array(entitlementRoleKeySchema).max(50).default([]),
    startsAt: z.string().datetime().optional(),
    expiresAt: z.string().datetime().optional(),
    usageLimit: z.number().int().positive().max(10_000_000).optional(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
}).superRefine((entitlement, context) => {
    if (entitlement.startsAt && entitlement.expiresAt &&
        Date.parse(entitlement.expiresAt) <= Date.parse(entitlement.startsAt)) {
        context.addIssue({
            code: "custom",
            path: ["expiresAt"],
            message: "授权到期时间必须晚于生效时间"
        });
    }
});
export const salesforceGrantPurposeSchema = z.enum(["identity", "org_management", "business_data"]);
export const salesforceOAuthGrantSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    platformUserId: z.string().uuid(),
    salesforceUserId: z.string().min(15).max(18),
    purpose: salesforceGrantPurposeSchema,
    status: z.enum(["active", "revoked", "expired"]),
    scopes: z.array(z.string().min(1).max(120)).max(30),
    instanceUrl: z.string().url(),
    expiresAt: z.string().datetime().optional(),
    lastUsedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const organizationConnectionSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    displayName: z.string().min(1).max(160),
    status: z.enum(["pending", "connected", "attention", "disabled"]),
    cloudVariant: z.enum(["global", "alibaba_cloud"]).default("global"),
    loginUrl: z.string().url().optional(),
    myDomainUrl: z.string().url().optional(),
    integrationMode: z.literal("read_only"),
    authType: z.preprocess((value) => value === "client_credentials_legacy" ? "client_credentials" : value, z.enum(["jwt_bearer", "client_credentials", "authorization_code_pkce"])),
    userAuthMode: z.literal("authorization_code_pkce").default("authorization_code_pkce"),
    integrationAuthMode: z.enum(["jwt_bearer", "client_credentials", "authorization_code_pkce"]).default("jwt_bearer"),
    businessPermissionSetApiNames: z.array(salesforceApiNameSchema).max(50).default([]),
    instanceUrl: z.string().url().optional(),
    apiVersion: z.string().regex(/^v\d{2,3}\.\d$/).optional(),
    trustedHosts: z.array(z.string().min(1).max(253)).max(20).default([]),
    isSandbox: z.boolean().optional(),
    lastVerifiedAt: z.string().datetime().optional(),
    lastError: z.string().max(1000).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const modelProviderSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    credentialSourceProviderId: z.string().uuid().optional(),
    name: z.string().min(1).max(120),
    kind: z.enum(["openai", "dashscope", "deepseek", "moonshot", "zhipu", "openai_compatible", "salesforce_openai_gateway"]),
    protocol: z.enum(["responses", "chat_completions"]).default("responses"),
    baseUrl: z.string().url(),
    authMode: z.enum(["bearer", "api-key"]),
    credentialLocation: z.enum(["platform_vault", "salesforce_external_credential"]).default("platform_vault"),
    providerSchemaVersion: z.string().regex(/^\d+\.\d+$/).optional(),
    region: z.string().max(80).optional(),
    workspace: z.string().max(160).optional(),
    requestTimeoutMs: z.number().int().min(5_000).max(120_000).default(25_000),
    maxRetries: z.number().int().min(0).max(5).default(2),
    status: z.enum(["verified", "invalid", "disabled"]),
    capabilities: z.object({
        chat: z.boolean(),
        vision: z.boolean(),
        structuredOutput: z.boolean(),
        jsonSchema: z.boolean().default(false),
        toolCalling: z.boolean().default(false),
        chinese: z.boolean().optional(),
        documentUnderstanding: z.boolean().optional()
    }),
    capabilityProbe: z.object({
        schemaVersion: z.literal("1.0"),
        model: z.string().min(1).max(160),
        checkedAt: z.string().datetime(),
        latencyMs: z.number().int().nonnegative(),
        checks: z.object({
            text: z.enum(["supported", "unsupported", "not_tested"]),
            chinese: z.enum(["supported", "unsupported", "not_tested"]),
            structuredPlanning: z.enum(["supported", "unsupported", "not_tested"]),
            jsonOutput: z.enum(["supported", "unsupported", "not_tested"]),
            jsonSchema: z.enum(["supported", "unsupported", "not_tested"]),
            toolCalling: z.enum(["supported", "unsupported", "not_tested"]),
            vision: z.enum(["supported", "unsupported", "not_tested"]),
            documentUnderstanding: z.enum(["supported", "unsupported", "not_tested"]),
            ocr: z.enum(["supported", "unsupported", "not_tested"]).default("not_tested"),
            speechToText: z.enum(["supported", "unsupported", "not_tested"]).default("not_tested")
        })
    }).optional(),
    verifiedAt: z.string().datetime().optional(),
    lastError: z.string().max(1000).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const modelCatalogItemSchema = z.object({
    id: z.string().min(1).max(160),
    label: z.string().min(1).max(160),
    ownedBy: z.string().max(160).optional(),
    createdAt: z.string().datetime().optional(),
    description: z.string().max(1000).optional(),
    capabilities: z.array(z.string().min(1).max(80)).max(30).optional(),
    features: z.array(z.string().min(1).max(80)).max(30).optional(),
    inputModalities: z.array(z.string().min(1).max(80)).max(20).optional(),
    outputModalities: z.array(z.string().min(1).max(80)).max(20).optional()
});
export const modelCatalogSchema = z.object({
    schemaVersion: z.literal("1.0"),
    source: z.literal("provider"),
    providerKind: modelProviderSchema.shape.kind,
    models: z.array(modelCatalogItemSchema).max(500),
    fetchedAt: z.string().datetime(),
    manualEntrySupported: z.literal(true)
});
export const salesforceModelCredentialStatusSchema = z.object({
    available: z.boolean(),
    configured: z.boolean(),
    storage: z.literal("salesforce_external_credential"),
    externalCredential: z.literal("AI_OpenAI_External"),
    principalName: z.literal("AI_OpenAI_Named_Principal"),
    authorizationSource: z.enum(["admin_oauth", "integration_user"]).optional(),
    reason: z.string().max(1000).optional()
});
export const modelRoleSchema = z.enum(["main_reasoning", "image_document", "ocr_parser", "speech_to_text", "fallback"]);
export const salesforceModelCredentialMigrationStatusSchema = z.object({
    state: z.enum(["not_required", "blocked", "ready_to_revoke", "verification_required", "revoked"]),
    legacyProviderCount: z.number().int().nonnegative(),
    legacyRoles: z.array(modelRoleSchema),
    activeLegacyRoles: z.array(modelRoleSchema),
    missingRuntimeRoles: z.array(modelRoleSchema),
    unmappedLegacyProviderCount: z.number().int().nonnegative(),
    authorizationReady: z.boolean(),
    credentialConfigured: z.boolean().nullable()
});
export const modelProfileSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    providerId: z.string().uuid(),
    name: z.string().min(1).max(120),
    model: z.string().min(1).max(160),
    purpose: z.enum(["assistant", "attachment", "test"]),
    role: modelRoleSchema.optional(),
    maxContextTokens: z.number().int().min(1_024).max(2_000_000).default(32_000),
    maxOutputTokens: z.number().int().min(128).max(100_000).default(4_096),
    fallbackProviderId: z.string().uuid().optional(),
    fallbackModel: z.string().min(1).max(160).optional(),
    enabled: z.boolean(),
    isDefault: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const objectIntegrationOperationSchema = z.enum(["query", "aggregate", "create", "update", "delete"]);
export const objectIntegrationSelectionSchema = z.object({
    organizationId: z.string().min(15).max(18),
    objectApiName: salesforceApiNameSchema,
    isCustom: z.boolean(),
    operations: z.array(objectIntegrationOperationSchema).min(1).max(5).default(["query", "aggregate"]),
    permissionSetApiNames: z.array(salesforceApiNameSchema).max(50).default([]),
    exportPolicy: z.enum(["inherit", "allow", "deny"]).optional(),
    enabled: z.boolean(),
    maintenanceState: z.enum(["active", "paused", "removed"]),
    selectedBy: z.string().uuid(),
    updatedAt: z.string().datetime()
});
/**
 * V1.0 compatibility alias. V1.1 exposes this resource as an object
 * integration selection and no longer creates default records.
 */
export const orgScanScopeSchema = objectIntegrationSelectionSchema;
export const objectDirectoryEntrySchema = z.object({
    objectApiName: salesforceApiNameSchema,
    label: z.string().min(1).max(240),
    labelPlural: z.string().min(1).max(240),
    custom: z.boolean(),
    queryable: z.boolean(),
    createable: z.boolean(),
    updateable: z.boolean(),
    deletable: z.boolean(),
    selected: z.boolean(),
    selectedOperations: z.array(objectIntegrationOperationSchema).max(5).default([])
});
export const catalogFieldSchema = z.object({
    apiName: salesforceApiNameSchema,
    label: z.string().min(1).max(240),
    type: z.string().min(1).max(80),
    length: z.number().int().nonnegative().optional(),
    precision: z.number().int().nonnegative().optional(),
    scale: z.number().int().nonnegative().optional(),
    nillable: z.boolean(),
    createable: z.boolean(),
    updateable: z.boolean(),
    filterable: z.boolean(),
    sortable: z.boolean(),
    relationshipName: salesforceApiNameSchema.optional(),
    referenceTo: z.array(salesforceApiNameSchema).max(20).optional(),
    picklistValues: z.array(z.object({
        value: z.string().max(255),
        label: z.string().max(255),
        active: z.boolean()
    })).max(MAX_CATALOG_PICKLIST_VALUES).default([])
});
export const objectCatalogSchema = z.object({
    objectApiName: salesforceApiNameSchema,
    label: z.string().min(1).max(240),
    labelPlural: z.string().min(1).max(240),
    custom: z.boolean(),
    queryable: z.boolean(),
    createable: z.boolean(),
    updateable: z.boolean(),
    deletable: z.boolean(),
    fields: z.array(catalogFieldSchema).max(1000),
    recordTypes: z.array(catalogRecordTypeSchema).max(500).optional(),
    relationships: z.array(catalogRelationshipSchema).max(1000).optional()
});
export const catalogValidationRuleSchema = z.object({
    objectApiName: salesforceApiNameSchema,
    name: z.string().min(1).max(240),
    active: z.boolean(),
    errorMessage: z.string().max(1000).optional(),
    description: z.string().max(1000).optional(),
    errorDisplayField: salesforceApiNameSchema.optional(),
    referencedFields: z.array(salesforceApiNameSchema).max(200).optional()
});
export const catalogLayoutRequirementSchema = z.object({
    objectApiName: salesforceApiNameSchema,
    layoutName: z.string().min(1).max(240),
    requiredFields: z.array(salesforceApiNameSchema).max(200)
});
export const catalogDuplicateRuleSchema = z.object({
    objectApiName: salesforceApiNameSchema,
    name: z.string().min(1).max(240),
    label: z.string().min(1).max(240),
    active: z.boolean()
});
export const permissionSetAccessSchema = z.object({
    permissionSetApiName: salesforceApiNameSchema,
    objectApiName: salesforceApiNameSchema,
    read: z.boolean(),
    create: z.boolean(),
    update: z.boolean(),
    delete: z.boolean(),
    viewAll: z.boolean(),
    modifyAll: z.boolean(),
    readableFields: z.array(salesforceApiNameSchema).max(1000),
    editableFields: z.array(salesforceApiNameSchema).max(1000)
});
export const organizationSnapshotSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    status: z.enum(["running", "completed", "failed"]),
    trigger: z.enum(["initial", "manual", "scheduled", "pre_publish", "post_deployment"]),
    objectCatalogs: z.array(objectCatalogSchema),
    layoutRequirements: z.array(catalogLayoutRequirementSchema).max(5000).optional(),
    validationRules: z.array(catalogValidationRuleSchema).max(5000).default([]),
    duplicateRules: z.array(catalogDuplicateRuleSchema).max(1000).default([]),
    permissionMatrix: z.array(permissionSetAccessSchema).max(10000).default([]),
    scanWarnings: z.array(z.string().min(1).max(1000)).max(100).default([]),
    schemaHash: z.string().length(64),
    error: z.string().max(1000).optional(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().optional()
});
export const organizationDiffSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    fromSnapshotId: z.string().uuid(),
    toSnapshotId: z.string().uuid(),
    severity: z.enum(["none", "info", "warning", "blocking", "high_risk"]),
    changes: z.array(z.object({
        objectApiName: salesforceApiNameSchema,
        fieldApiName: salesforceApiNameSchema.optional(),
        kind: z.enum(["object_added", "object_removed", "object_access_changed", "field_added", "field_removed", "field_changed", "picklist_changed", "record_type_changed", "relationship_changed", "permission_changed", "validation_rule_changed", "duplicate_rule_changed", "layout_requirement_changed"]),
        severity: z.enum(["info", "warning", "blocking", "high_risk"]),
        summary: z.string().min(1).max(500)
    })).max(5000),
    createdAt: z.string().datetime()
});
export const skillCandidateSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    snapshotId: z.string().uuid(),
    objectApiName: salesforceApiNameSchema,
    name: z.string().min(1).max(160),
    description: z.string().min(1).max(1000),
    recommendedOperations: z.array(z.enum(["query", "aggregate", "create", "update", "delete"])).min(1),
    recommendedReadableFields: z.array(salesforceApiNameSchema).max(100),
    recommendedWritableFields: z.array(salesforceApiNameSchema).max(100),
    requiredFields: z.array(salesforceApiNameSchema).max(100),
    clarificationPrompts: z.array(z.string().min(1).max(500)).max(20),
    risks: z.array(z.string().min(1).max(500)).max(50),
    status: z.enum(["suggested", "accepted", "dismissed", "stale"]),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const skillTestCaseResultSchema = z.object({
    name: z.string().min(1).max(160),
    status: z.enum(["passed", "failed"]),
    qualityGate: z.enum([
        "query", "aggregate", "write_preview", "cross_object_sequence",
        "permission_denied", "unopened_object", "ambiguity", "required_fields",
        "validation_rule", "cancellation", "duplicate", "planning_timeout",
        "invalid_model_output"
    ]).optional(),
    targetObjectApiName: salesforceApiNameSchema.optional(),
    expectedOutcome: z.enum(["action_sequence", "no_action", "planning_error", "planning_timeout"]).optional(),
    actualOutcome: z.enum(["action_sequence", "no_action", "planning_error", "planning_timeout"]).optional(),
    expectedOperation: z.string().min(1).max(40).optional(),
    expectedObject: salesforceApiNameSchema.optional(),
    actualOperation: z.string().max(40).optional(),
    actualObject: salesforceApiNameSchema.optional(),
    expectedActionSequence: z.array(z.object({ operation: z.string().min(1).max(40), objectApiName: salesforceApiNameSchema })).max(20).optional(),
    actualActionSequence: z.array(z.object({ operation: z.string().min(1).max(40), objectApiName: salesforceApiNameSchema })).max(20).optional(),
    answer: z.string().max(4000).optional(),
    error: z.string().max(1000).optional(),
    durationMs: z.number().int().nonnegative()
});
export const testRunSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    skillKey: z.string().min(2).max(80),
    skillVersion: z.string().min(1).max(40),
    testDatasetId: z.string().uuid().optional(),
    testDatasetVersion: z.string().regex(/^\d+\.\d+\.\d+$/).optional(),
    snapshotId: z.string().uuid().optional(),
    status: z.enum(["running", "passed", "failed"]),
    model: z.string().max(160).optional(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    results: z.array(skillTestCaseResultSchema).max(500),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    completedAt: z.string().datetime().optional()
});
export const auditEventSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    actorUserId: z.string().uuid(),
    action: z.string().min(1).max(120),
    targetType: z.string().min(1).max(80),
    targetId: z.string().min(1).max(200),
    outcome: z.enum(["success", "failure"]),
    details: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.string().datetime()
});
export const executionAttemptSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    platformUserId: z.string().uuid(),
    salesforceUserId: z.string().min(15).max(18),
    actionId: z.string().uuid(),
    scenarioId: z.string().uuid(),
    scenarioVersion: z.string().min(1).max(40),
    idempotencyKey: z.string().length(64),
    attempt: z.number().int().min(1).max(100),
    status: z.enum(["in_progress", "succeeded", "failed"]),
    errorCode: z.string().max(120).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    completedAt: z.string().datetime().optional()
});
export const metadataFieldTypeSchema = z.enum([
    "Checkbox",
    "Currency",
    "Date",
    "DateTime",
    "LongTextArea",
    "Number",
    "Percent",
    "Picklist",
    "Text"
]);
export const createCustomFieldOperationSchema = z.object({
    kind: z.literal("create_custom_field"),
    objectApiName: salesforceApiNameSchema,
    fieldApiName: z.string().regex(/^[A-Za-z][A-Za-z0-9_]*__c$/),
    label: z.string().min(1).max(80),
    type: metadataFieldTypeSchema,
    description: z.string().max(1000).optional(),
    inlineHelpText: z.string().max(510).optional(),
    required: z.boolean().default(false),
    unique: z.boolean().default(false),
    externalId: z.boolean().default(false),
    defaultValue: z.union([z.string().max(255), z.number(), z.boolean()]).optional(),
    length: z.number().int().min(1).max(32768).optional(),
    visibleLines: z.number().int().min(2).max(50).optional(),
    precision: z.number().int().min(1).max(18).optional(),
    scale: z.number().int().min(0).max(17).optional(),
    picklistValues: z.array(z.object({
        fullName: z.string().min(1).max(255),
        label: z.string().min(1).max(255),
        isDefault: z.boolean().default(false)
    })).min(1).max(200).optional()
}).superRefine((value, ctx) => {
    if (value.type === "Text" && value.length === undefined) {
        ctx.addIssue({ code: "custom", path: ["length"], message: "文本字段必须指定长度" });
    }
    if (value.type === "LongTextArea" && (value.length === undefined || value.visibleLines === undefined)) {
        ctx.addIssue({ code: "custom", path: ["length"], message: "长文本字段必须指定长度和可见行数" });
    }
    if (["Currency", "Number", "Percent"].includes(value.type) && (value.precision === undefined || value.scale === undefined || value.scale >= value.precision)) {
        ctx.addIssue({ code: "custom", path: ["precision"], message: "数字字段必须指定有效的精度和小数位" });
    }
    if (value.type === "Picklist" && !value.picklistValues?.length) {
        ctx.addIssue({ code: "custom", path: ["picklistValues"], message: "选项字段至少需要一个选项值" });
    }
    if (value.required && value.defaultValue === undefined) {
        ctx.addIssue({ code: "custom", path: ["required"], message: "必填字段必须提供默认值，避免现有记录失效" });
    }
});
export const setFieldPermissionOperationSchema = z.object({
    kind: z.literal("set_field_permission"),
    permissionSetApiName: salesforceApiNameSchema,
    objectApiName: salesforceApiNameSchema,
    fieldApiName: salesforceApiNameSchema,
    readable: z.boolean().default(true),
    editable: z.boolean().default(false)
}).superRefine((value, ctx) => {
    if (value.editable && !value.readable) {
        ctx.addIssue({ code: "custom", path: ["readable"], message: "可编辑字段必须同时可读" });
    }
});
export const addFieldToLayoutOperationSchema = z.object({
    kind: z.literal("add_field_to_layout"),
    layoutFullName: z.string().trim().min(3).max(255),
    objectApiName: salesforceApiNameSchema,
    fieldApiName: salesforceApiNameSchema,
    sectionLabel: z.string().trim().min(1).max(80),
    behavior: z.enum(["Edit", "Readonly", "Required"]).default("Edit")
});
export const metadataOperationSchema = z.discriminatedUnion("kind", [
    createCustomFieldOperationSchema,
    setFieldPermissionOperationSchema,
    addFieldToLayoutOperationSchema
]);
export const metadataChangeRequestSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    title: z.string().min(1).max(160),
    description: z.string().max(1000).optional(),
    status: z.enum(["draft", "validating", "validated", "deploying", "deployed", "failed", "cancelled"]),
    operations: z.array(metadataOperationSchema).min(1).max(60),
    changeHash: z.string().length(64),
    sourceSnapshotId: z.string().uuid(),
    validationRunId: z.string().uuid().optional(),
    deploymentRunId: z.string().uuid().optional(),
    warnings: z.array(z.string().min(1).max(1000)).max(100).default([]),
    lastError: z.string().max(2000).optional(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deployedAt: z.string().datetime().optional()
});
export const metadataDeploymentRunSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    changeRequestId: z.string().uuid(),
    mode: z.enum(["validate", "deploy"]),
    phase: z.enum(["retrieving", "deploying"]).optional(),
    status: z.enum(["queued", "in_progress", "succeeded", "failed", "cancelled"]),
    salesforceJobId: z.string().min(15).max(18),
    changeHash: z.string().length(64),
    sourceComponentHash: z.string().length(64).optional(),
    componentSuccesses: z.number().int().nonnegative().default(0),
    componentFailures: z.array(z.object({
        componentType: z.string().max(160).optional(),
        fullName: z.string().max(500).optional(),
        problemType: z.string().max(160).optional(),
        problem: z.string().min(1).max(2000)
    })).max(500).default([]),
    warnings: z.array(z.string().min(1).max(2000)).max(500).default([]),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    completedAt: z.string().datetime().optional()
});
export const attachmentMimeTypeSchema = z.enum([
    "application/pdf",
    "image/jpeg",
    "image/png",
    "text/plain",
    "text/csv",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
]);
/**
 * Attachment bytes live in the configured object store. This record is the
 * tenant-scoped, durable control plane used for ownership, lifecycle and audit.
 */
export const attachmentSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    conversationId: z.string().uuid(),
    uploadedBy: z.string().min(1).max(100),
    fileName: z.string().min(1).max(120),
    mimeType: attachmentMimeTypeSchema,
    sizeBytes: z.number().int().min(1).max(20 * 1024 * 1024),
    sha256: z.string().length(64),
    objectKey: z.string().min(1).max(900),
    status: z.enum(["uploading", "ready", "consumed", "deleted", "failed"]),
    uploadMode: z.enum(["server", "presigned"]),
    actionId: z.string().uuid().optional(),
    failureReason: z.string().max(1000).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime(),
    consumedAt: z.string().datetime().optional()
});
export const batchItemResultSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    jobId: z.string().uuid(),
    actionId: z.string().uuid(),
    sourceIndex: z.number().int().min(1).max(500).optional(),
    recordId: z.string().min(15).max(18).optional(),
    status: z.enum(["succeeded", "failed", "conflict", "excluded"]),
    retryable: z.boolean(),
    attempt: z.number().int().min(1).max(100),
    errorCode: z.string().max(120).optional(),
    message: z.string().max(1000).optional(),
    result: z.record(z.string(), z.unknown()).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const batchJobSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().min(15).max(18),
    conversationId: z.string().uuid(),
    actionId: z.string().uuid(),
    initiatedBy: z.string().min(1).max(100),
    operation: z.enum(["create", "update"]),
    objectApiName: salesforceApiNameSchema,
    status: z.enum(["executing", "succeeded", "partial", "needs_attention", "cancelled", "expired"]),
    requested: z.number().int().min(1).max(50),
    submitted: z.number().int().min(0).max(50),
    succeeded: z.number().int().min(0).max(50),
    failed: z.number().int().min(0).max(50),
    excluded: z.number().int().min(0).max(50),
    attemptCount: z.number().int().min(1).max(100),
    idempotencyKey: z.string().length(64),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    completedAt: z.string().datetime().optional()
});
export const runtimeActionSummarySchema = z.object({
    id: z.string().uuid(),
    conversationId: z.string().uuid(),
    conversationTitle: z.string().min(1).max(160),
    salesforceUserId: z.string().min(1).max(100),
    operation: z.enum(["query", "aggregate", "create", "update", "delete"]),
    objectApiName: salesforceApiNameSchema,
    state: z.enum(["draft", "pending_confirmation", "needs_attention", "executing", "succeeded", "failed", "cancelled", "expired"]),
    version: z.number().int().positive(),
    itemCount: z.number().int().positive().max(50),
    error: z.string().max(1000).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime()
});
export const platformRuntimeOverviewSchema = z.object({
    organizationId: z.string().min(15).max(18),
    metrics: z.object({
        conversations: z.number().int().nonnegative(),
        conversations24h: z.number().int().nonnegative(),
        actions: z.number().int().nonnegative(),
        pendingConfirmation: z.number().int().nonnegative(),
        needsAttention: z.number().int().nonnegative(),
        succeeded: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        batchJobs: z.number().int().nonnegative(),
        batchPartialOrFailed: z.number().int().nonnegative()
    }),
    recentActions: z.array(runtimeActionSummarySchema).max(100),
    batchJobs: z.array(batchJobSchema).max(100),
    evaluatedAt: z.string().datetime()
});
